import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";

import { useSelectedTimeRange } from "@/features/dashboard/store";
import { BLUE, colorGenerator, GREEN } from "@/features/shared/widgets/graphs/helpers";
import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { renderValue } from "@/features/dashboard/components/timeseries/render-value";
import { DimensionValue } from "@/lib/dimensions";
import { offBlack } from "@/theme/colors";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import {
  chartSeriesIsDisabled,
  chartSeriesPresentationColor,
  formatChartSeriesName,
  getChartSeriesPresentationState,
  resolveSeriesDisplayLabel,
} from "@/components/graph/chart-series-presentation";
import { useTranslation } from "@/lib/i18n";

import useGraphOptions from "./use-graph-options";
import { resolveGraphChartHeight } from "./resolve-graph-chart-height";
import { useTimeSeriesNoData } from "./hooks/use-time-series-no-data";
import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";
import { AvaIndexTimeSeriesConfig } from "./graph.types";
import { AVA_INDEX_AXIS_MAX, avaIndexColor } from "./ava-index-colors";

interface AvaIndexGraphProps {
  config: AvaIndexTimeSeriesConfig;
  data: ParameterTimeSeriesChartResult[];
  currentTime: DimensionValue;
  timeBounds?: ChartTimeBounds;
  height?: number;
}

/**
 * Unitless index chart: severity-colored bars with a black trend line over
 * them. Bar color carries the band; the line is just the shape of the series.
 */
const AvaIndexGraph = ({
  config,
  data,
  currentTime,
  timeBounds,
  height,
}: AvaIndexGraphProps) => {
  const { t } = useTranslation();
  const timeRange = useSelectedTimeRange();

  const seriesWithData = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    const greenColorGenerator = colorGenerator(GREEN);
    const blueColorGenerator = colorGenerator(BLUE);
    const getColor = (color: string) => {
      const fallback = "grey";
      if (color === "green") {
        return greenColorGenerator.next().value ?? fallback;
      }
      if (color === "blue") {
        return blueColorGenerator.next().value ?? fallback;
      }
      return fallback;
    };

    return config.series.flatMap((seriesConfig): Highcharts.SeriesOptionsType[] => {
      const dataByParameter = data.find(
        (timeSeriesResult) => timeSeriesResult.parameter.id === seriesConfig.parameter_id,
      );
      const presentationState = getChartSeriesPresentationState(dataByParameter, timeRange);
      const displayLabel = resolveSeriesDisplayLabel(seriesConfig, dataByParameter);
      const serieName = formatChartSeriesName(displayLabel, presentationState, t);
      const disabled = chartSeriesIsDisabled(presentationState);
      const lineColor = chartSeriesPresentationColor(
        presentationState,
        getColor(seriesConfig.color),
      );
      const datapoints = dataByParameter?.data?.datapoints ?? [];
      const mappedDatapoints = datapoints.map((datapoint) => ({
        x: +new Date(datapoint.timestamp),
        y: datapoint.value,
        // A disabled series is greyed out wholesale, so severity colors are
        // dropped to avoid implying live data.
        color: disabled ? undefined : avaIndexColor(datapoint.value),
      }));

      // The trend line owns the legend entry so the swatch is a black line
      // matching what is drawn, instead of a single color chip that cannot
      // represent the bars' per-datapoint severity colors. The bars link to it
      // by id, so one legend toggle still hides both.
      const lineSeriesId = `ava-index-line-${seriesConfig.parameter_id}`;

      return [
        {
          type: "column",
          name: serieName,
          // Only surfaces for a disabled series, where per-datapoint severity
          // colors are dropped; live bars are colored per point.
          color: lineColor,
          linkedTo: lineSeriesId,
          enableMouseTracking: !disabled,
          tooltip: {
            pointFormatter: function (this) {
              return `${this.series.name}: <strong>${renderValue(
                this.y,
                config.unit,
              )}</strong>`;
            },
          },
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
          data: mappedDatapoints,
        },
        {
          type: "line",
          id: lineSeriesId,
          name: serieName,
          // Black reads as the trend across the bars instead of competing with
          // their severity colors; a disabled series keeps the greyed-out tone
          // so it still matches its colorless bars.
          color: disabled ? lineColor : offBlack,
          // The bars own the tooltip so hovering does not report the value twice.
          enableMouseTracking: false,
          marker: { enabled: false },
          data: mappedDatapoints.map(({ x, y }) => ({ x, y })),
        },
      ];
    });
  }, [config, data, timeRange, t]);

  const chartHeight = resolveGraphChartHeight(height);
  const graphOptions = useGraphOptions(
    {
      ...(chartHeight !== undefined ? { chart: { height: chartHeight } } : {}),
      title: {
        text: config.title,
      },
      // Fixed 0–100 so the severity bands sit at the same height regardless of
      // the values actually in range (getYAxisPerUnit has no rule for unitless).
      yAxis: [
        {
          min: 0,
          max: AVA_INDEX_AXIS_MAX,
          title: { text: "" },
        },
      ],
      xAxis: [
        {
          plotLines: [
            {
              id: "currentTime",
              color: "#01689B", // KNMI blue
              value: +new Date(currentTime.queryStringValue),
              zIndex: 4,
            },
          ],
        },
      ],
      plotOptions: {
        column: {
          grouping: false,
          groupPadding: 0,
          // Columns grow from the axis threshold, so an index of 0 would render
          // a zero-height bar and read as missing data instead of "green". A
          // minimum length keeps the severity color visible at the baseline.
          minPointLength: 3,
        },
      },
      series: seriesWithData,
    },
    timeBounds,
  );

  const noData = useTimeSeriesNoData(config, data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact highcharts={Highcharts} options={graphOptions} />
    </NoDataOverlay>
  );
};

export default AvaIndexGraph;
